import { useMutation } from "@tanstack/react-query";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";

export interface UserUpdate {
    userId: string;
    emailAddress?: string;
    sendPasswordResetEmail: boolean;
    roles?: string[];
    active?: boolean;
    initials: string | null;
    updatedInitials: boolean;
    vip: boolean | undefined;
    breakerUserId: string | null;
    silentUserId: string | null;
    silentAccountTransfers: boolean | undefined;
    frozenAccount: boolean | undefined;
}

const useUserUpdate = () => {
    const update = useAuthenticatedRequestCreator<boolean, UserUpdate>(
        (model) => {
            const formData = new FormData();
            for (const [key, value] of Object.entries(model)) {
                if (value !== undefined && value !== null) {
                    formData.append(key, value);
                }
            }

            return {
                method: "patch",
                url: "/account",
                data: formData,
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            };
        }
    );

    const removeMfa = useAuthenticatedRequestCreator<boolean, string>(
        (userId) => ({
            method: 'post',
            url: '/account/remove-mfa',
            data: { userId },
        })
    );

    const unlock = useAuthenticatedRequestCreator<boolean, string>(
        (userId) => ({
            method: 'post',
            url: '/account/unlock',
            data: { userId }
        })
    );

    const { mutateAsync: updateUser } = useMutation((model: UserUpdate) =>
        update(model)
    );

    const { mutateAsync: removeUserTwoFactor } = useMutation((userId: string) =>
        removeMfa(userId)
    );

    const { mutateAsync: unlockAccount } = useMutation((userId: string) =>
        unlock(userId)
    );

    return { updateUser, removeUserTwoFactor, unlockAccount };
};

export default useUserUpdate;
